/**
 * Kreator obszarów i modułów — kontrakt danych (Krok 0).
 *
 * Definicja szablonu modułu którego admin ITE projektuje w panelu administracyjnym
 * i instaluje u klientów. Wzorzec hierarchii: obszar → moduł → encja → (pola | widoki).
 *
 * Używane przez:
 *   - apps/api/lib/validators/module-builder.ts   (Zod schemas dla endpointów admin)
 *   - apps/api/app/api/admin/module-areas/...     (CRUD obszarów)
 *   - apps/api/app/api/admin/modules/[code]/...   (rozszerzenie istniejącego CRUD modułów)
 *   - apps/admin/src/pages/admin/ModuleTemplate*  (UI kreatora w panelu ITE)
 *
 * Decyzje projektowe (z planu MODULE_BUILDER_PLAN.md):
 *   - Dane biznesowe rekordów → fizyczne tabele u klienta (NIE jeden wór JSONB).
 *   - Format widoku `list` = pełny `ListConfig` Kreatora Listy (hub Kreatora Krok 0,
 *     ADR-0050 — odwrócenie wcześniejszej decyzji o własnym formacie; stary format czytany
 *     przez konwerter z `module-builder-views.ts`). Widok `form` ma własny `formConfig`
 *     (drzewo elementów adresowane slugiem pola — kontrakt tamże).
 *   - Wersje szablonu → dwie równoległe: dla klientów (1.1, 1.2, 2.1...) i techniczna (2.1.001).
 *   - Encja może albo tworzyć nową tabelę u klienta (`new_table`), albo referencyjnie
 *     podpinać się pod istniejącą encję systemową (`reference_to_system_entity`,
 *     np. moduł Urlopy referuje istniejących pracowników).
 *
 * Plan: docs/phases/MODULE_BUILDER_PLAN.md + docs/phases/MODULE_BUILDER_HUB_PLAN.md (Krok 0)
 */

import type { ListConfig } from './list-view';
import type { MenuItem } from './menu';
import type { ModuleTemplateFormConfig } from './module-builder-views';
import {
  collectTemplateFormConfigProblems,
  collectTemplateListConfigProblems,
  ensureTemplateListConfig,
} from './module-builder-views';

// ============================================================================
// Stałe (single source of truth — używane przez walidator Zod, frontend, baza)
// ============================================================================

/** Typy pól encji szablonu. Mirror z field_type w `module_template_entity_fields`. */
export const MODULE_TEMPLATE_FIELD_TYPES = [
  'text',
  'longtext',
  'number',
  'decimal',
  'date',
  'datetime',
  'boolean',
  'select',
  'multi_select',
  'reference',
  'file',
  'image',
] as const;

export type ModuleTemplateFieldType = (typeof MODULE_TEMPLATE_FIELD_TYPES)[number];

/** Tryb pokazywania encji u klienta. */
export const MODULE_TEMPLATE_NAV_MODES = [
  'tab', // encja jako zakładka wewnątrz strony modułu
  'sidebar_entry', // encja jako osobna pozycja w sidebarze klienta
  'hidden', // encja niewidoczna w UI (tylko backend dla relacji między encjami)
] as const;

export type ModuleTemplateNavMode = (typeof MODULE_TEMPLATE_NAV_MODES)[number];

/** Strategia tworzenia tabeli encji u klienta przy instalacji szablonu. */
export const MODULE_TEMPLATE_DDL_STRATEGIES = [
  'new_table', // tworzymy nową fizyczną tabelę w schemacie klienta
  'reference_to_system_entity', // podpinamy się pod istniejącą encję systemową (employees itp.)
  'reference_to_creator_entity', // (Etap 3) odwołanie do kartoteki Kreatora z INNEGO szablonu
  'document_class', // kartoteka na bazie KLASY DOKUMENTU (np. wewnętrzne) zawężona do typu systemowego;
  //                   rekordy trafiają do wspólnej tabeli `documents`, a nie do osobnej tabeli
] as const;

export type ModuleTemplateDdlStrategy = (typeof MODULE_TEMPLATE_DDL_STRATEGIES)[number];

/** Typy widoków nad encją. */
export const MODULE_TEMPLATE_VIEW_TYPES = [
  'list', // klasyczna lista z kolumnami
  'calendar', // kalendarz (data od/do na encji)
  'kanban', // tablica kanban (kolumny = wartości pola statusu)
  'dashboard', // dashboard z widgetami
  'form', // sam formularz (do encji edytowanych pojedynczo, bez listy)
] as const;

export type ModuleTemplateViewType = (typeof MODULE_TEMPLATE_VIEW_TYPES)[number];

// ============================================================================
// Limity (sprawdzane przez walidator Zod)
// ============================================================================

/**
 * Limit Postgres dla identyfikatora to 63 znaki.
 *
 * Pattern nazwy tabeli encji u klienta:
 *   `<tenant_code>_module_<template_slug>_<entity_slug>` lub jego wariant `_ext`.
 *
 * Tenant_code = 6 znaków. „_module_" + dwa separatory + „_ext" = 13 + 5 = 18 znaków
 * narzutu. Na sumę (template_slug + entity_slug) zostaje 44 znaki. Dzielimy ostrożnie
 * — slug szablonu max 20, slug encji max 20, jest margines na manewry.
 */
export const MODULE_TEMPLATE_SLUG_MAX_LENGTH = 20;
export const MODULE_TEMPLATE_ENTITY_SLUG_MAX_LENGTH = 20;
export const MODULE_TEMPLATE_FIELD_SLUG_MAX_LENGTH = 40;

export const MODULE_TEMPLATE_NAME_MAX_LENGTH = 100;
export const MODULE_TEMPLATE_DESCRIPTION_MAX_LENGTH = 1000;

/** Max liczba encji w jednym szablonie modułu. */
export const MODULE_TEMPLATE_MAX_ENTITIES = 20;

/** Max liczba pól w jednej encji szablonu. */
export const MODULE_TEMPLATE_MAX_FIELDS_PER_ENTITY = 50;

/** Max liczba widoków w jednej encji szablonu. */
export const MODULE_TEMPLATE_MAX_VIEWS_PER_ENTITY = 10;

/**
 * Format slugu — pasuje do walidatora w lib/custom-modules/ddl.ts (musi się zaczynać
 * od litery, dalej litera/cyfra/podkreślenie).
 */
export const MODULE_TEMPLATE_SLUG_PATTERN = /^[a-z][a-z0-9_]*$/;

// ============================================================================
// Typy struktury szablonu (czyste TS — używane też w frontend SPA)
// ============================================================================

/** Opcja w polu typu select / multi_select. */
export interface ModuleTemplateFieldOption {
  value: string;
  label: string;
  /**
   * Kolor hex opcji (`#RRGGBB`), np. do kolorowania statusów w kalendarzu/Kanbanie
   * (parytet z `custom_module_field_options.color` u klienta — patrz `install.ts`).
   * Opcjonalny — brak = kolor domyślny/szary w rendererach (`CalendarView`, `KanbanBoard`).
   */
  color?: string;
}

/**
 * Wiązanie parametru słownika zewnętrznego (etap 2 modelu dwuetapowego, ADR-0068) —
 * mirror `DictionaryParamBinding` z `apps/api/lib/dictionaries/external-source-types.ts`.
 * Legacy: goły string w mapie wiązań = `{ kind: 'record_field', fieldSlug: <string> }`
 * (stary kształt sprzed Fazy 2b — backend nadal go czyta, NOWE zapisy są tagowane).
 */
export type ModuleTemplateDictionaryParamBinding =
  | { kind: 'record_field'; fieldSlug: string }
  | { kind: 'context.company' }
  | { kind: 'context.user' }
  | { kind: 'constant'; value: string | number | boolean };

/** Definicja pojedynczego pola encji szablonu. */
export interface ModuleTemplateFieldDefinition {
  slug: string;
  name: string;
  type: ModuleTemplateFieldType;
  isRequired: boolean;
  description?: string;
  defaultValue?: string | null;
  /** Wymagane gdy type === 'select' lub 'multi_select' — chyba że pole ma `dictionaryCode`. */
  options?: ModuleTemplateFieldOption[];
  /**
   * Kod słownika globalnego (`dictionaries.code`, katalog ITE) źródła opcji dla
   * type === 'select' | 'multi_select'. Gdy ustawiony — pole NIE wymaga `options`
   * inline (opcje pochodzą ze słownika w runtime). NULL/undefined = wbudowane `options`.
   */
  dictionaryCode?: string | null;
  /**
   * Nadpisanie sposobu wyświetlania pozycji słownika NA POZIOMIE POLA (Pakiet 1c).
   * Precedencja przy renderze: `dictionaryDisplayMode ?? <display_mode słownika>`.
   * NULL/undefined = użyj trybu zdefiniowanego na samym słowniku. Do bazy zawsze
   * zapisywany jest KOD pozycji — to ustawienie dotyczy wyłącznie prezentacji.
   */
  dictionaryDisplayMode?: 'label' | 'code' | 'code_label' | null;
  /**
   * Wiązania parametrów słownika zewnętrznego (ADR-0066, rozszerzone Fazą 2b ADR-0068 do
   * modelu dwuetapowego) — mapa NAZWA PARAMETRU źródła → wiązanie. Wartość: tagowane
   * `ModuleTemplateDictionaryParamBinding` (nowe zapisy) ALBO goły string = legacy
   * `record_field` ze slugiem pola-sąsiada (stare wiersze jsonb — czytane bez migracji).
   * NULL/undefined = brak wiązań (słownik ręczny albo zewnętrzny bez parametrów).
   */
  dictionaryParamBindings?: Record<string, string | ModuleTemplateDictionaryParamBinding> | null;
  /**
   * Wymagane gdy type === 'reference'. Wskazuje encję docelową — slug innej encji
   * w tym samym szablonie ALBO slug istniejącej encji systemowej (np. 'employees').
   */
  referenceTargetSlug?: string;
  sortOrder: number;
  /**
   * Czy pole figuruje w najnowszym OPUBLIKOWANYM snapshocie szablonu (add-only po
   * publikacji — odpowiednik `firstPublishedAt` pól kartoteki systemowej). Pole
   * WYLICZANE, ustawiane wyłącznie przez GET .../[code] (load-definition); Zod PUT
   * structure je wycina, a `canonicalStructureJson` pomija (nie trafia do snapshotów).
   * true → UI blokuje edycję i usunięcie pola (dozwolone: dodawanie nowych + reorder),
   * a PUT structure odrzuca usunięcie / zmianę typu (409).
   */
  isPublished?: boolean;
}

/** Definicja pojedynczego widoku nad encją szablonu. */
export interface ModuleTemplateViewDefinition {
  slug: string;
  name: string;
  type: ModuleTemplateViewType;
  isDefault: boolean;
  sortOrder: number;
  /**
   * Wypełnione gdy type === 'list' — pełny format Kreatora Listy (ADR-0050). Kolumny
   * adresowane slugiem pola encji (`column.id`) + wbudowane `record_name`/`record_number`;
   * wbudowane nieobecne w configu runtime klienta dokleja na końcu listy. Stary format
   * (`{ columns: [{ fieldSlug, ... }] }`) czytany przez konwerter z `module-builder-views.ts`.
   */
  listConfig?: ListConfig;
  /**
   * Wypełnione (opcjonalnie) gdy type === 'form' — układ formularza, drzewo elementów
   * adresowane slugiem pola (kontrakt w `module-builder-views.ts`). Starsze widoki `form`
   * bez configu = pusty układ (klient dostaje domyślny formularz).
   */
  formConfig?: ModuleTemplateFormConfig;
}

/** Definicja pojedynczej encji w szablonie modułu. */
export interface ModuleTemplateEntityDefinition {
  /**
   * UUID encji (`module_template_entities.id`) — klucz biblioteki widoków kartoteki
   * dla encji `new_table` (ADR-0060 Faza 2; slug encji jest edytowalny, więc niestabilny).
   * Ustawiane przez GET .../[code] (editor); w PUT body odcinane przez walidator.
   * W snapshotach wersji może występować (zapis menu zamraża encje z loadera — jak
   * `isPublished`); instalacja/aktualizacja go ignoruje.
   */
  id?: string;
  slug: string;
  name: string;
  icon?: string;
  sortOrder: number;
  navigationMode: ModuleTemplateNavMode;
  ddlStrategy: ModuleTemplateDdlStrategy;
  /** Wymagane gdy ddlStrategy === 'reference_to_system_entity'. */
  referenceSystemModuleSlug?: string;
  /**
   * Cel odwołania cross-szablonowego (Etap 3) — wymagane gdy
   * ddlStrategy === 'reference_to_creator_entity'. Para wskazuje encję `new_table`
   * innego szablonu Kreatora: kod szablonu-źródła + slug encji w nim.
   */
  referenceTemplateCode?: string;
  referenceTargetEntitySlug?: string;
  /**
   * Wymagane gdy ddlStrategy === 'document_class'. Kod klasy dokumentu, na której bazuje
   * kartoteka (mirror `DocumentClass` z lib/documents/class-module.ts — np. 'internal').
   * Rekordy tej kartoteki to wiersze wspólnej tabeli `documents` z tą klasą.
   */
  documentClass?: string;
  /**
   * Wymagane gdy ddlStrategy === 'document_class'. UUID systemowego (globalnego) typu
   * dokumentu (`global_document_types.id` w ADM), do którego kartoteka jest zawężona
   * (np. typ DELEGACJE w klasie 'internal'). Zawężenie widoku listy po tym typie = slice 2.
   */
  documentGlobalTypeId?: string;
  /**
   * Per-modułowy override bogatego layoutu kartoteki systemowej (System B).
   * Obecne tylko gdy ddlStrategy === 'reference_to_system_entity'.
   * Undefined / null → użyj domyślnego layoutu z system_kartoteka_layouts.
   * Zapisywany przez PUT .../entities/[entitySlug]/kartoteka-layout.
   */
  kartotekaLayoutOverride?: Record<string, unknown>;
  /**
   * S6 (Etap 4) — czy encja figuruje w najnowszym opublikowanym snapshocie modułu.
   * Ustawiane przez GET .../[code] (editor); brak w PUT body / snapshocie wersji.
   * true → UI ukrywa kosz (encja opublikowana = trwała; chroniona bramą PUT structure).
   * false / undefined → pełna edycja dozwolona.
   */
  isPublished?: boolean;
  fields: ModuleTemplateFieldDefinition[];
  views: ModuleTemplateViewDefinition[];
}

/** Definicja całego szablonu modułu w bazie administracyjnej (ADM). */
export interface ModuleTemplateDefinition {
  slug: string;
  name: string;
  /** Kod obszaru do którego należy szablon (np. 'hr'). */
  areaCode: string;
  /** Numer wersji widoczny dla klientów (np. '1.0', '1.2', '2.1'). */
  clientVersion: string;
  /** Numer wersji techniczny (np. '2.1.001'). Auto-bump przy każdym zapisie. */
  technicalVersion: string;
  description?: string;
  icon?: string;
  color?: string;
  entities: ModuleTemplateEntityDefinition[];
  /**
   * Domyślne menu modułu — fragment drzewa pozycji, który po instalacji pojawia się
   * w obszarze modułu w (automatycznym) lewym pasku klienta: sekcje, kolejność, ikony,
   * linki, encje modułu jako pozycje. `module`-pozycje referują encje slugiem szablonu
   * (`moduleCode` = slug encji), rozwiązywanym przy renderze do tenantowego custom-modułu.
   * Nieobecne / puste = dotychczasowa płaska lista encji (auto-assembly bez zmian).
   * Paletka edytora oferuje tylko: `module` (encje) + `section` + `separator` + `custom_link`.
   */
  defaultMenu?: MenuItem[];
  /**
   * Stan publikacji szablonu (zwracany przez GET edytora; nieobecny w snapshotach instalacji).
   * Pozwala UI pokazać trwale, czy klienci mają bieżącą strukturę, czy są niewypublikowane zmiany.
   */
  publishState?: ModuleTemplatePublishState;
  /**
   * Autorstwo szablonu (zwracane przez GET edytora; nieobecne w snapshotach instalacji).
   * Pozwala pokazać kto utworzył i kto ostatnio edytował moduł.
   */
  author?: ModuleTemplateAuthor;
  /**
   * Nazwa WYBRANEGO menu modułu (`defaultMenu`, jadącego do klientów); `null` = „Domyślne menu".
   * Zwracane przez GET edytora; nieobecne w snapshotach instalacji.
   */
  defaultMenuName?: string | null;
  /**
   * Menu ROBOCZE modułu (szkice ITE zapisane w bazie, NIE jadą do klientów). „Wybierz" zamienia
   * menu robocze z wybranym. Zwracane przez GET edytora; nieobecne w snapshotach instalacji.
   */
  draftMenus?: ModuleTemplateDraftMenu[];
}

/** Menu robocze szablonu modułu (panel ITE, `module_menus`). */
export interface ModuleTemplateDraftMenu {
  id: string;
  name: string;
  hierarchy: MenuItem[];
  /** ISO timestamp ostatniego zapisu. */
  updatedAt: string;
}

/** Kto utworzył / ostatnio edytował szablon (imię i nazwisko admina ITE, fallback email). */
export interface ModuleTemplateAuthor {
  /** Imię i nazwisko twórcy (fallback email); `null` gdy nieznany (np. szablon seedowany). */
  createdByName: string | null;
  /** ISO timestamp utworzenia szablonu; `null` gdy nieznany. */
  createdAt: string | null;
  /** Imię i nazwisko ostatniego edytującego (fallback email); `null` gdy nieznany. */
  updatedByName: string | null;
  /** ISO timestamp ostatniej edycji; `null` gdy nieznany. */
  updatedAt: string | null;
}

export interface ModuleTemplatePublishState {
  /** Najnowsza opublikowana dla klientów wersja X.Y; `null` = nigdy nie publikowano. */
  latestPublishedVersion: string | null;
  /** ISO timestamp ostatniej publikacji; `null` gdy nigdy nie publikowano. */
  latestPublishedAt: string | null;
  /**
   * Czy snapshot BIEŻĄCEJ wersji technicznej jest już opublikowany. `false` przy istniejącej
   * publikacji = zapisano zmiany po ostatnim „Opublikuj" (klienci ich jeszcze nie mają).
   */
  currentSnapshotPublished: boolean;
}

// ============================================================================
// Walidacja struktury szablonu (czysta funkcja — używana przez edytor w SPA)
// ============================================================================

/**
 * Waga problemu w strukturze szablonu.
 *   - 'error'   → blokuje zapis (szablon jest „popsuty", backend i tak go odrzuci 422).
 *   - 'warning' → nie blokuje zapisu, ale sygnalizuje że coś jest niedokończone/podejrzane.
 */
export type TemplateValidationSeverity = 'error' | 'warning';

/**
 * Kody problemów. Każdy ma odpowiednik w i18n (`moduleTemplateEditor.validation.<code>`).
 * Błędy ('error') są LUSTREM twardych reguł walidatora Zod z
 * `apps/api/lib/validators/module-builder.ts` — jeśli zmienisz reguły tam, zaktualizuj tutaj.
 * Ostrzeżenia ('warning') są dodatkiem UI (backend ich nie sprawdza).
 */
export type TemplateValidationCode =
  // --- errors (mirror Zod) ---
  | 'template.duplicate_entity_slug'
  | 'entity.missing_system_reference'
  | 'entity.invalid_system_reference_slug'
  | 'entity.missing_creator_reference'
  | 'entity.missing_document_class'
  | 'entity.duplicate_field_slug'
  | 'entity.duplicate_view_slug'
  | 'field.select_without_options'
  | 'field.reference_without_target'
  | 'view.list_without_columns'
  | 'view.list_unknown_column'
  | 'view.form_field_without_slug'
  | 'view.form_unknown_field'
  | 'view.form_duplicate_element_id'
  | 'view.form_too_deep'
  | 'view.form_no_fields'
  | 'view.multiple_defaults'
  // --- warnings (tylko UI) ---
  | 'template.no_entities'
  | 'entity.no_fields'
  | 'field.reference_target_not_found'
  | 'field.required_creator_reference';

/** Pojedynczy problem wykryty w strukturze szablonu. */
export interface TemplateValidationIssue {
  severity: TemplateValidationSeverity;
  code: TemplateValidationCode;
  /** Slug encji której dotyczy problem (null = problem na poziomie całego szablonu). */
  entitySlug: string | null;
  /** Slug pola, jeśli problem dotyczy konkretnego pola. */
  fieldSlug?: string;
  /** Slug widoku, jeśli problem dotyczy konkretnego widoku. */
  viewSlug?: string;
  /** Parametry do interpolacji w komunikacie i18n (np. nazwa pola, typ widoku). */
  params?: Record<string, string | number>;
}

/**
 * Sprawdza strukturę szablonu (listę encji) i zwraca listę problemów.
 *
 * Czysta funkcja bez zależności — używana w edytorze szablonu (SPA), żeby:
 *   - zablokować przycisk „Zapisz" gdy są błędy (zanim backend odrzuci 422),
 *   - wskazać KONKRETNĄ encję/pole/widok z problemem (czego sam toast 422 nie robi),
 *   - pokazać miękkie ostrzeżenia (pusta encja, brak pól) zanim ITE wyśle szablon.
 *
 * Reguły 'error' są lustrem walidatora Zod (`apps/api/lib/validators/module-builder.ts`).
 * Reguły 'warning' to dodatek UI — backend ich nie egzekwuje.
 *
 * Format slugu, długości, max liczby — celowo NIE są tu sprawdzane: edytor wymusza je
 * polami formularza / limitami, a backend ma ostateczne słowo. Tu skupiamy się na
 * semantyce „czy szablon ma sens jako całość".
 */
export function validateModuleTemplateStructure(
  entities: ModuleTemplateEntityDefinition[],
): TemplateValidationIssue[] {
  const issues: TemplateValidationIssue[] = [];

  // --- warning: szablon bez encji ---
  if (entities.length === 0) {
    issues.push({ severity: 'warning', code: 'template.no_entities', entitySlug: null });
  }

  // --- error: duplikaty slugów encji w obrębie szablonu ---
  const seenEntitySlugs = new Set<string>();
  for (const entity of entities) {
    if (seenEntitySlugs.has(entity.slug)) {
      issues.push({
        severity: 'error',
        code: 'template.duplicate_entity_slug',
        entitySlug: entity.slug,
        params: { slug: entity.slug },
      });
    }
    seenEntitySlugs.add(entity.slug);
  }

  // Zbiór wszystkich slugów encji — do sprawdzania celów referencji.
  const entitySlugSet = new Set(entities.map((e) => e.slug));
  // Mapa slug → encja — do rozpoznania, czy cel referencji to odwołanie do kartoteki Kreatora.
  const entityBySlug = new Map(entities.map((e) => [e.slug, e]));

  for (const entity of entities) {
    // --- error: encja referencyjna bez slugu encji systemowej ---
    if (entity.ddlStrategy === 'reference_to_system_entity') {
      const ref = entity.referenceSystemModuleSlug?.trim();
      if (!ref) {
        issues.push({
          severity: 'error',
          code: 'entity.missing_system_reference',
          entitySlug: entity.slug,
        });
      } else if (!MODULE_TEMPLATE_SLUG_PATTERN.test(ref)) {
        issues.push({
          severity: 'error',
          code: 'entity.invalid_system_reference_slug',
          entitySlug: entity.slug,
          params: { slug: ref },
        });
      }
    }

    // --- error: odwołanie do kartoteki Kreatora bez kompletnej pary (Etap 3) ---
    // Samo istnienie celu + new_table + brak self-reference sprawdza backend (ma DB).
    if (entity.ddlStrategy === 'reference_to_creator_entity') {
      const code = entity.referenceTemplateCode?.trim();
      const target = entity.referenceTargetEntitySlug?.trim();
      if (!code || !target) {
        issues.push({
          severity: 'error',
          code: 'entity.missing_creator_reference',
          entitySlug: entity.slug,
        });
      }
    }

    // --- error: kartoteka na bazie klasy dokumentu bez klasy ---
    // Typ systemowy (documentGlobalTypeId) jest OPCJONALNY — element może być kotwicą całej
    // klasy, a zawężenie do typu(-ów) dokumentu robi filtr na pozycji menu (`document_type_id`).
    if (entity.ddlStrategy === 'document_class') {
      const cls = entity.documentClass?.trim();
      if (!cls) {
        issues.push({
          severity: 'error',
          code: 'entity.missing_document_class',
          entitySlug: entity.slug,
        });
      }
    }

    // --- warning: encja tworząca nową tabelę, ale bez żadnego pola ---
    if (entity.ddlStrategy === 'new_table' && entity.fields.length === 0) {
      issues.push({ severity: 'warning', code: 'entity.no_fields', entitySlug: entity.slug });
    }

    // --- pola ---
    const seenFieldSlugs = new Set<string>();
    for (const field of entity.fields) {
      // error: duplikat slugu pola
      if (seenFieldSlugs.has(field.slug)) {
        issues.push({
          severity: 'error',
          code: 'entity.duplicate_field_slug',
          entitySlug: entity.slug,
          fieldSlug: field.slug,
          params: { slug: field.slug },
        });
      }
      seenFieldSlugs.add(field.slug);

      // error: select/multi_select bez opcji I bez słownika globalnego (dictionaryCode)
      if (
        (field.type === 'select' || field.type === 'multi_select') &&
        (!Array.isArray(field.options) || field.options.length === 0) &&
        !field.dictionaryCode?.trim()
      ) {
        issues.push({
          severity: 'error',
          code: 'field.select_without_options',
          entitySlug: entity.slug,
          fieldSlug: field.slug,
          params: { name: field.name, type: field.type },
        });
      }

      // reference: cel wymagany (error), a jeśli jest — czy wskazuje encję w szablonie (warning)
      if (field.type === 'reference') {
        const target = field.referenceTargetSlug?.trim();
        if (!target) {
          issues.push({
            severity: 'error',
            code: 'field.reference_without_target',
            entitySlug: entity.slug,
            fieldSlug: field.slug,
            params: { name: field.name },
          });
        } else if (!entitySlugSet.has(target)) {
          // Cel może być encją systemową (rozwiązywaną przy instalacji), więc to tylko ostrzeżenie.
          issues.push({
            severity: 'warning',
            code: 'field.reference_target_not_found',
            entitySlug: entity.slug,
            fieldSlug: field.slug,
            params: { name: field.name, target },
          });
        } else if (
          field.isRequired &&
          entityBySlug.get(target)?.ddlStrategy === 'reference_to_creator_entity'
        ) {
          // Etap 3 (haczyk §2): wymagane pole celujące w kartotekę Kreatora — u klienta
          // bez szablonu-źródła nie ma czego wybrać. Traktujemy jako NIE-blokujące do
          // instalacji źródła, ale ostrzegamy projektanta.
          issues.push({
            severity: 'warning',
            code: 'field.required_creator_reference',
            entitySlug: entity.slug,
            fieldSlug: field.slug,
            params: { name: field.name, target },
          });
        }
      }
    }

    // --- widoki ---
    const entityFieldSlugs = new Set(entity.fields.map((f) => f.slug));
    // Encja-odwołanie ma fields: [] — jej pola żyją w kartotece-celu (systemowej lub Kreatora),
    // nie w modelu szablonu. Sprawdzanie istnienia pól w configach widoków pomijamy.
    const viewCollectorOptions =
      entity.ddlStrategy === 'reference_to_system_entity' ||
      entity.ddlStrategy === 'reference_to_creator_entity' ||
      entity.ddlStrategy === 'document_class'
        ? { allowUnknownFields: true }
        : undefined;
    const seenViewSlugs = new Set<string>();
    const defaultCountByType = new Map<string, number>();
    for (const view of entity.views) {
      // error: duplikat slugu widoku
      if (seenViewSlugs.has(view.slug)) {
        issues.push({
          severity: 'error',
          code: 'entity.duplicate_view_slug',
          entitySlug: entity.slug,
          viewSlug: view.slug,
          params: { slug: view.slug },
        });
      }
      seenViewSlugs.add(view.slug);

      // widok listy: konwerter toleruje stary format, potem reguły wspólne z Zod
      if (view.type === 'list') {
        const listConfig = ensureTemplateListConfig(view.listConfig);
        if (!listConfig) {
          issues.push({
            severity: 'error',
            code: 'view.list_without_columns',
            entitySlug: entity.slug,
            viewSlug: view.slug,
            params: { name: view.name },
          });
        } else {
          for (const problem of collectTemplateListConfigProblems(
            listConfig,
            entityFieldSlugs,
            viewCollectorOptions,
          )) {
            if (problem.kind === 'no_columns') {
              issues.push({
                severity: 'error',
                code: 'view.list_without_columns',
                entitySlug: entity.slug,
                viewSlug: view.slug,
                params: { name: view.name },
              });
            } else {
              issues.push({
                severity: 'error',
                code: 'view.list_unknown_column',
                entitySlug: entity.slug,
                viewSlug: view.slug,
                params: { name: view.name, column: problem.columnId },
              });
            }
          }
        }
      }

      // układ formularza: reguły wspólne z Zod (nieznane slugi, duplikaty, głębokość)
      if (view.formConfig) {
        for (const problem of collectTemplateFormConfigProblems(
          view.formConfig,
          entityFieldSlugs,
          undefined,
          viewCollectorOptions,
        )) {
          issues.push(formProblemToIssue(problem, entity.slug, view.slug, view.name));
        }
      }

      if (view.isDefault) {
        defaultCountByType.set(view.type, (defaultCountByType.get(view.type) ?? 0) + 1);
      }
    }

    // error: więcej niż jeden domyślny widok danego typu
    defaultCountByType.forEach((count, type) => {
      if (count > 1) {
        issues.push({
          severity: 'error',
          code: 'view.multiple_defaults',
          entitySlug: entity.slug,
          params: { type, count },
        });
      }
    });
  }

  return issues;
}

/** Mapuje problem układu formularza (kolektor z module-builder-views) na issue mirroru. */
function formProblemToIssue(
  problem: ReturnType<typeof collectTemplateFormConfigProblems>[number],
  entitySlug: string,
  viewSlug: string,
  viewName: string,
): TemplateValidationIssue {
  const base = { severity: 'error' as const, entitySlug, viewSlug };
  switch (problem.kind) {
    case 'field_without_slug':
      return {
        ...base,
        code: 'view.form_field_without_slug',
        params: { name: viewName, element: problem.elementId },
      };
    case 'unknown_field':
      return {
        ...base,
        code: 'view.form_unknown_field',
        params: { name: viewName, slug: problem.slug },
      };
    case 'duplicate_element_id':
      return {
        ...base,
        code: 'view.form_duplicate_element_id',
        params: { name: viewName, element: problem.id },
      };
    case 'too_deep':
      return {
        ...base,
        code: 'view.form_too_deep',
        params: { name: viewName, depth: problem.depth },
      };
    case 'no_fields':
      return { ...base, code: 'view.form_no_fields', params: { name: viewName } };
  }
}

// ============================================================================
// Normalizacja configów widoków (read-path + porównania snapshotów)
// ============================================================================

/**
 * Przepuszcza configi widoków wszystkich encji przez konwerter starego formatu listy
 * (`ensureTemplateListConfig`). Używane wszędzie tam, gdzie struktura szablonu może
 * pochodzić sprzed zmiany formatu (ADR-0050): odczyt snapshotów historii wersji
 * (instalacja, dedup zapisu, diff aktualizacji — Krok 3), żeby porównania nie zgłaszały
 * fałszywej „zmiany widoku" przy samej zmianie formatu.
 */
export function normalizeTemplateViewConfigs(
  entities: ModuleTemplateEntityDefinition[],
): ModuleTemplateEntityDefinition[] {
  return entities.map((entity) => ({
    ...entity,
    views: entity.views.map((view) => ({
      ...view,
      listConfig: view.type === 'list' ? ensureTemplateListConfig(view.listConfig) : undefined,
    })),
  }));
}
